import type { QueryParams } from '@/lib/types/common.types';
import type { CreateRequestInput, Request, UpdateRequestInput } from '@/lib/types/request.types';
import { transformBackendRequest } from '@/lib/utils/requestTransformers';
import apiClient from './client';
import { extractArrayResponse, extractResponseData, handleApiError } from './responseHelpers';

/**
 * Maps frontend request status to backend request status
 * Frontend uses: 'open', 'in_progress', 'completed', 'cancelled', 'closed', 'published', 'draft'
 * Backend uses: 'published', 'in_bidding', 'draft', 'closed', 'cancelled', 'completed'
 */
function mapFrontendStatusToBackend(status?: string): string | undefined {
  if (!status || status === '') {
    return undefined; // No filter - backend will return published/in_bidding by default
  }

  const statusMap: Record<string, string> = {
    open: 'published', // 'open' means published requests
    in_progress: 'in_bidding', // 'in_progress' means in_bidding
    published: 'published',
    draft: 'draft',
    closed: 'closed',
    cancelled: 'cancelled',
    completed: 'completed',
  };

  return statusMap[status] || undefined;
}

export const requestsApi = {
  // Request Management
  getAll: async (params?: QueryParams): Promise<Request[]> => {
    try {
      // Map frontend status to backend status
      const backendParams: QueryParams = { ...params };
      if (params?.status) {
        const backendStatus = mapFrontendStatusToBackend(params.status as string);
        if (backendStatus) {
          backendParams.status = backendStatus;
        } else {
          // Remove invalid status filter
          delete backendParams.status;
        }
      }

      const response = await apiClient.get<{ data: Request[]; pagination?: any }>('/v1/requests', {
        params: backendParams,
      });

      // Extract array from response using helper
      const requestsArray = extractArrayResponse<any>(response);

      // ✅ Defensive check: ensure requestsArray is always an array
      if (!Array.isArray(requestsArray)) {
        console.warn(
          '⚠️ requestsApi.getAll: requestsArray is not an array, returning empty array',
          {
            requestsArray,
            type: typeof requestsArray,
          }
        );
        return [];
      }

      // Transform all requests from snake_case to camelCase
      return requestsArray.map((req) => transformBackendRequest(req));
    } catch (error) {
      handleApiError(error);
    }
  },

  getById: async (id: string): Promise<Request> => {
    try {
      const response = await apiClient.get<{ data: any }>(`/v1/requests/${id}`);

      // Extract data from response using helper
      const backendRequest = extractResponseData<any>(response);

      // Transform from snake_case to camelCase
      return transformBackendRequest(backendRequest);
    } catch (error) {
      handleApiError(error);
    }
  },

  create: async (requestData: CreateRequestInput): Promise<Request> => {
    // Validate required fields (only title and description)
    if (!requestData.title || requestData.title.trim() === '') {
      throw new Error('REQUEST_TITLE_REQUIRED');
    }
    if (!requestData.description || requestData.description.trim() === '') {
      throw new Error('REQUEST_DESCRIPTION_REQUIRED');
    }

    // Prepare data (transformation from camelCase to snake_case is handled automatically by interceptor)
    const requestPayload: Record<string, any> = {
      title: requestData.title.trim(),
      description: requestData.description.trim(),
    };

    if (requestData.categoryId) {
      requestPayload.categoryId = requestData.categoryId.trim();
    }
    if (typeof requestData.expectedDuration === 'number') {
      requestPayload.expectedDuration = requestData.expectedDuration;
    }
    if (requestData.requiresOnSiteVisit !== undefined) {
      requestPayload.requiresOnSiteVisit = requestData.requiresOnSiteVisit;
    }

    if (requestData.deadline) {
      requestPayload.deadline = requestData.deadline;
    }

    // Add optional fields (camelCase - will be transformed automatically)
    if (requestData.budgetMin !== undefined && requestData.budgetMin !== null) {
      requestPayload.budgetMin = requestData.budgetMin;
    }
    if (requestData.budgetMax !== undefined && requestData.budgetMax !== null) {
      requestPayload.budgetMax = requestData.budgetMax;
    }
    if (requestData.preferredStartDate) {
      requestPayload.preferredStartDate = requestData.preferredStartDate;
    }
    if (requestData.location) {
      // Convert location from string to object if needed
      // Backend expects location as map[string]interface{}, not string
      if (typeof requestData.location === 'string') {
        requestPayload.location = {
          city: requestData.location,
        };
      } else {
        // Already an object, use as is
        requestPayload.location = requestData.location;
      }
    }
    // AI fields (optional)
    if (requestData.ai_enhanced_description) {
      requestPayload.ai_enhanced_description = requestData.ai_enhanced_description;
    }
    if (requestData.ai_project_phases && requestData.ai_project_phases.length > 0) {
      requestPayload.ai_project_phases = requestData.ai_project_phases;
    }
    // Send ai_bill_of_quantities if it exists and has items (even if some are incomplete)
    if (
      requestData.ai_bill_of_quantities &&
      Array.isArray(requestData.ai_bill_of_quantities) &&
      requestData.ai_bill_of_quantities.length > 0
    ) {
      // Filter out completely empty items (all fields empty) but keep items with at least one field filled
      const validBoqItems = requestData.ai_bill_of_quantities.filter((item: any) => {
        return (
          item &&
          ((item.description && item.description.trim() !== '') ||
            (item.quantity && item.quantity > 0) ||
            (item.unit && item.unit.trim() !== '') ||
            (item.specifications && item.specifications.trim() !== ''))
        );
      });

      if (validBoqItems.length > 0) {
        requestPayload.ai_bill_of_quantities = validBoqItems;
        console.log('📊 Sending bill of quantities:', validBoqItems.length, 'items');
      } else {
        console.log('⚠️ Bill of quantities array is empty or all items are empty, skipping');
      }
    }
    if (requestData.ai_payment_schedule && requestData.ai_payment_schedule.length > 0) {
      requestPayload.ai_payment_schedule = requestData.ai_payment_schedule;
    }
    if (requestData.ai_suggested_timeline) {
      requestPayload.ai_suggested_timeline = requestData.ai_suggested_timeline;
    }
    if (requestData.ai_applied_fields && requestData.ai_applied_fields.length > 0) {
      requestPayload.ai_applied_fields = requestData.ai_applied_fields;
    }
    const officialBooklet = requestData.official_booklet ?? (requestData as any).officialBooklet;
    if (officialBooklet !== undefined) {
      requestPayload.official_booklet = officialBooklet;
    }
    // Add attachments URLs if present (from Cloudinary)
    if (requestData.attachments && requestData.attachments.length > 0) {
      // Check if attachments are URLs (strings) or Files
      const areUrls = requestData.attachments.every((att) => typeof att === 'string');

      if (areUrls) {
        // Attachments are URLs from Cloudinary - send as JSON
        requestPayload.attachments = requestData.attachments;
      } else {
        // Attachments are Files - use FormData for multipart/form-data
        const formData = new FormData();

        // Add all form fields (exclude attachments from requestPayload - they will be added as files)
        Object.entries(requestPayload).forEach(([key, value]) => {
          // Skip attachments field - it will be added as files, not as form field
          if (key === 'attachments' || key === 'attachment') {
            return;
          }
          if (value !== null && value !== undefined) {
            // Convert camelCase to snake_case for backend
            const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
            if (typeof value === 'object' && !(value instanceof File)) {
              const jsonValue = JSON.stringify(value);
              formData.append(snakeKey, jsonValue);
              // Log AI fields for debugging
              if (key === 'ai_bill_of_quantities' || snakeKey === 'ai_bill_of_quantities') {
                console.log(
                  '📊 Sending ai_bill_of_quantities via FormData:',
                  JSON.parse(jsonValue).length,
                  'items'
                );
              }
            } else {
              formData.append(snakeKey, String(value));
            }
          }
        });

        // Add attachments as files (not as form field)
        requestData.attachments.forEach((file) => {
          if (file && typeof file === 'object' && file instanceof File) {
            formData.append('attachments', file);
          }
        });

        // Log the data being sent for debugging
        console.log('📤 Sending request data with file attachments via FormData');

        try {
          // Don't set Content-Type header - let Axios set it automatically with boundary
          const response = await apiClient.post<{ message?: string; data: any }>(
            '/v1/requests',
            formData
          );
          console.log('✅ Request created successfully:', response.data);
          const backendRequest = response.data?.data || response.data;

          // Files are already saved in request.attachments (JSONB) by backend
          // No need to link separately

          return transformBackendRequest(backendRequest);
        } catch (error: any) {
          console.error('❌ Error creating request:', error);
          console.error('Error response:', error.response?.data);
          throw error;
        }
      }
    }

    // Send as JSON (with or without attachment URLs)
    // Log the data being sent for debugging
    console.log('📤 Sending request data to backend:', JSON.stringify(requestPayload, null, 2));

    try {
      // Transformation to snake_case happens automatically in apiClient interceptor
      const response = await apiClient.post<{ message?: string; data: any }>(
        '/v1/requests',
        requestPayload
      );
      console.log('✅ Request created successfully:', response.data);
      // Backend returns { message: "...", data: Request }
      const backendRequest = response.data?.data || response.data;

      // Files are already saved in request.attachments (JSONB) by backend
      // No need to link separately

      return transformBackendRequest(backendRequest);
    } catch (error: any) {
      console.error('❌ Error creating request:', error);
      console.error('Error response:', error.response?.data);
      console.error('Request data that failed:', JSON.stringify(requestPayload, null, 2));
      throw error;
    }
  },

  update: async (id: string, requestData: UpdateRequestInput): Promise<Request> => {
    // Prepare data for backend (convert camelCase to snake_case)
    const backendData: Record<string, any> = {};

    if (requestData.title !== undefined) {
      backendData.title = requestData.title;
    }
    if (requestData.description !== undefined) {
      backendData.description = requestData.description;
    }
    if (requestData.categoryId !== undefined) {
      backendData.category_id = requestData.categoryId;
    }
    if (requestData.budgetMin !== undefined) {
      backendData.budget_min = requestData.budgetMin;
    }
    if (requestData.budgetMax !== undefined) {
      backendData.budget_max = requestData.budgetMax;
    }
    if (requestData.expectedDuration !== undefined) {
      backendData.expected_duration = requestData.expectedDuration;
    }
    if (requestData.deadline !== undefined) {
      // Send deadline as YYYY-MM-DD (database expects date type, not timestamp)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (requestData.deadline && !dateRegex.test(requestData.deadline)) {
        throw new Error('INVALID_DATE_FORMAT: Deadline must be in YYYY-MM-DD format');
      }
      backendData.deadline = requestData.deadline;
    }
    if (requestData.preferredStartDate !== undefined) {
      // Send preferredStartDate as YYYY-MM-DD (database expects date type, not timestamp)
      if (requestData.preferredStartDate) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(requestData.preferredStartDate)) {
          throw new Error('INVALID_DATE_FORMAT: Preferred start date must be in YYYY-MM-DD format');
        }
        backendData.preferred_start_date = requestData.preferredStartDate;
      } else {
        backendData.preferred_start_date = null;
      }
    }
    if (requestData.requiresOnSiteVisit !== undefined) {
      backendData.requires_onsite_visit = requestData.requiresOnSiteVisit;
    }
    if (requestData.location !== undefined) {
      // Convert location from string to object if needed
      // Backend expects location as map[string]interface{}, not string
      if (typeof requestData.location === 'string') {
        backendData.location = {
          city: requestData.location,
        };
      } else {
        // Already an object, use as is
        backendData.location = requestData.location;
      }
    }
    // AI fields (optional)
    if (requestData.ai_enhanced_description !== undefined) {
      backendData.ai_enhanced_description = requestData.ai_enhanced_description;
    }
    if (requestData.ai_project_phases !== undefined) {
      backendData.ai_project_phases = requestData.ai_project_phases;
    }
    if (requestData.ai_bill_of_quantities !== undefined) {
      backendData.ai_bill_of_quantities = requestData.ai_bill_of_quantities;
    }
    if (requestData.ai_payment_schedule !== undefined) {
      backendData.ai_payment_schedule = requestData.ai_payment_schedule;
    }
    if (requestData.ai_suggested_timeline !== undefined) {
      backendData.ai_suggested_timeline = requestData.ai_suggested_timeline;
    }
    if (requestData.ai_applied_fields !== undefined) {
      backendData.ai_applied_fields = requestData.ai_applied_fields;
    }
    const officialBooklet = requestData.official_booklet ?? (requestData as any).officialBooklet;
    if (officialBooklet !== undefined) {
      backendData.official_booklet = officialBooklet;
    }
    // Check if there are attachments to upload
    if (requestData.attachments && requestData.attachments.length > 0) {
      // Check if attachments are URLs (strings) or Files
      const areUrls = requestData.attachments.every((att) => typeof att === 'string');

      if (areUrls) {
        // Attachments are URLs from Cloudinary - send as JSON
        backendData.attachments = requestData.attachments;
      } else {
        // Attachments are Files - use FormData for multipart/form-data
        const formData = new FormData();

        // Add all form fields (exclude attachments - they will be added as files)
        Object.entries(backendData).forEach(([key, value]) => {
          // Skip attachments field - it will be added as files, not as form field
          if (key === 'attachments' || key === 'attachment') {
            return;
          }
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !(value instanceof File)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, String(value));
            }
          }
        });

        // Add attachments as files (not as form field)
        requestData.attachments.forEach((file) => {
          if (file && typeof file === 'object' && file instanceof File) {
            formData.append('attachments', file);
          }
        });

        // Log the data being sent for debugging
        console.log('📤 Sending update request data with attachments via FormData');

        const response = await apiClient.put<{ message?: string; data: any }>(
          `/v1/requests/${id}`,
          formData,
          {
            headers: {},
          }
        );
        const backendRequest = response.data?.data || response.data;
        return transformBackendRequest(backendRequest);
      }
    }

    // No attachments, use JSON
    const response = await apiClient.put<{ message?: string; data: any }>(
      `/v1/requests/${id}`,
      backendData
    );
    // Backend returns { message: "...", data: Request }
    const backendRequest = response.data?.data || response.data;
    return transformBackendRequest(backendRequest);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/requests/${id}`);
  },

  publish: async (id: string): Promise<Request> => {
    const response = await apiClient.patch<{ message?: string; data?: any }>(
      `/v1/requests/${id}/publish`
    );
    const backendRequest = response.data?.data || response.data;
    return transformBackendRequest(backendRequest);
  },

  unpublish: async (id: string): Promise<Request> => {
    const response = await apiClient.patch<{ message?: string; data?: any }>(
      `/v1/requests/${id}/unpublish`
    );
    const backendRequest = response.data?.data || response.data;
    return transformBackendRequest(backendRequest);
  },

  close: async (id: string): Promise<Request> => {
    const response = await apiClient.patch<{ message?: string; data?: any }>(
      `/v1/requests/${id}/close`
    );
    const backendRequest = response.data?.data || response.data;
    return transformBackendRequest(backendRequest);
  },

  cancel: async (id: string): Promise<Request> => {
    const response = await apiClient.patch<{ message?: string; data?: any }>(
      `/v1/requests/${id}/cancel`
    );
    const backendRequest = response.data?.data || response.data;
    return transformBackendRequest(backendRequest);
  },

  extendDeadline: async (id: string, newDeadline: string): Promise<Request> => {
    // Validate date format (should be YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(newDeadline)) {
      throw new Error('INVALID_DATE_FORMAT: Deadline must be in YYYY-MM-DD format');
    }
    // Send deadline as YYYY-MM-DD (database expects date type, not timestamp)
    const response = await apiClient.patch<{ message?: string; data: Request } | Request>(
      `/v1/requests/${id}/extend-deadline`,
      { new_deadline: newDeadline } // Backend expects new_deadline field (YYYY-MM-DD format)
    );
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return transformBackendRequest((response.data as { data: any }).data);
    } else if (response.data && !('data' in response.data)) {
      return transformBackendRequest(response.data as any);
    } else {
      throw new Error('Invalid response format');
    }
  },

  // Attachments
  addAttachment: async (id: string, file: File): Promise<{ id: string; filePath: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<
      | { message?: string; data: { id: string; filePath: string } }
      | { id: string; filePath: string }
    >(`/v1/requests/${id}/attachments`, formData, {
      headers: {},
    });
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return (response.data as { data: { id: string; filePath: string } }).data;
    } else if (response.data && !('data' in response.data)) {
      return response.data as unknown as { id: string; filePath: string };
    } else {
      throw new Error('Invalid response format');
    }
  },

  deleteAttachment: async (id: string, attachmentId: string): Promise<void> => {
    await apiClient.delete(`/v1/requests/${id}/attachments/${attachmentId}`);
  },

  // Offer Selection
  selectOffer: async (id: string, offerId: string): Promise<Request> => {
    const response = await apiClient.post<{ message?: string; data?: any }>(
      `/v1/requests/${id}/select-offer`,
      { offerId }
    );
    const backendRequest = response.data?.data || response.data;
    return transformBackendRequest(backendRequest);
  },

  unselectOffer: async (id: string): Promise<Request> => {
    const response = await apiClient.delete<{ message?: string; data?: any }>(
      `/v1/requests/${id}/unselect-offer`
    );
    const backendRequest = response.data?.data || response.data;
    return transformBackendRequest(backendRequest);
  },

  // Public Search & Browse
  getActive: async (params?: QueryParams): Promise<Request[]> => {
    const response = await apiClient.get<{ data: Request[]; pagination?: any }>(
      '/v1/requests/active',
      { params }
    );
    // Backend returns { data: Request[], pagination: {...} }
    let requestsArray: any[] = [];
    if (Array.isArray(response.data)) {
      requestsArray = response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      requestsArray = response.data.data;
    } else {
      return [];
    }

    // ✅ Defensive check: ensure requestsArray is always an array
    if (!Array.isArray(requestsArray)) {
      console.warn(
        '⚠️ requestsApi.getActive: requestsArray is not an array, returning empty array',
        {
          requestsArray,
          type: typeof requestsArray,
        }
      );
      return [];
    }

    // Transform all requests from snake_case to camelCase
    return requestsArray.map((req) => transformBackendRequest(req));
  },

  getByCategory: async (categoryId: string, params?: QueryParams): Promise<Request[]> => {
    const response = await apiClient.get<{ data: Request[]; pagination?: any }>(
      `/v1/requests/category/${categoryId}`,
      {
        params,
      }
    );
    // Backend returns { data: Request[], pagination: {...} }
    let requestsArray: any[] = [];
    if (Array.isArray(response.data)) {
      requestsArray = response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      requestsArray = response.data.data;
    } else {
      return [];
    }

    // ✅ Defensive check: ensure requestsArray is always an array
    if (!Array.isArray(requestsArray)) {
      console.warn(
        '⚠️ requestsApi.getByCategory: requestsArray is not an array, returning empty array',
        {
          requestsArray,
          type: typeof requestsArray,
        }
      );
      return [];
    }

    // Transform all requests from snake_case to camelCase
    return requestsArray.map((req) => transformBackendRequest(req));
  },

  getOffers: async (id: string, params?: QueryParams) => {
    const response = await apiClient.get<{ data: any[]; pagination?: any }>(
      `/v1/offers/request/${id}`,
      { params }
    );
    // Backend returns { data: Offer[], pagination: {...} }
    let offersArray: any[] = [];
    if (Array.isArray(response.data)) {
      offersArray = response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      offersArray = response.data.data;
    } else {
      console.warn('⚠️ Unexpected response format:', response.data);
      return [];
    }
    return offersArray;
  },

  incrementViews: async (id: string): Promise<void> => {
    await apiClient.patch(`/v1/requests/${id}/increment-views`);
  },

  // My Requests
  getMyRequests: async (params?: QueryParams): Promise<Request[]> => {
    try {
      const response = await apiClient.get<{ data: Request[]; pagination?: any }>(
        '/v1/requests/me',
        { params }
      );

      // Extract array from response using helper
      const requestsArray = extractArrayResponse<any>(response);

      // ✅ Defensive check: ensure requestsArray is always an array
      if (!Array.isArray(requestsArray)) {
        console.warn(
          '⚠️ requestsApi.getMyRequests: requestsArray is not an array, returning empty array',
          {
            requestsArray,
            type: typeof requestsArray,
          }
        );
        return [];
      }

      // Transform all requests from snake_case to camelCase
      return requestsArray.map((req) => transformBackendRequest(req));
    } catch (error) {
      handleApiError(error);
    }
  },

  getMyStatistics: async () => {
    const response = await apiClient.get<{ data: any } | any>('/v1/requests/me/statistics');
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return (response.data as { data: any }).data;
    } else if (response.data && !('data' in response.data)) {
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  },
};
